<template>
    <div class="tile" :data-position="position"
        :class="{
            'in-move-range': isInMoveRange,
            'control-base': isControlBase,
            [controlMarker]: controlMarker,
            'controlled': controlMarker,
            'dark': isFourPlayerTile,
            'occupied': units.length,
            'active-tile': activeTile,
            'in-charge-range': isInChargeRange,
            'fortified': fortInPosition,
            [`action-count-${tileActions}`]: tileActions > 1,
            'has-maneuver-action': hasManeuverAction,
            'is-attackable': fortIsAttackable || fortIsSacrificeAttackable,
            'mimicable-unit': mimicableUnitOnPosition,
            [theme ? theme.toLowerCase() : '']: theme,
        }"
    >
        <div v-if="controlMarker" class="control-marker">
            <img
                v-if="!gameState.theme"
                :src="getAssetUrl(`../../assets/control_marker_${controlMarker}.png`)"
                draggable="false"
            />
            <img
                v-if="gameState.theme"
                :src="getAssetUrl(`../../assets/control_marker_${controlMarker}_alt.png`)"
                class="alt-img"
                draggable="false"
            />
        </div>
        <div class="inner-wrapper">
            <div class="action-icon-wrapper" v-if="canBeDeployedTo">
                <div
                    class="btn-icon-group"
                    @click="onCommand('deploy')"
                >
                    <fa-icon class="action-icon arrow-icon" icon="long-arrow-alt-down" />
                    <icon class="action-icon helmet-icon" icon="knight-helmet" />
                </div>
            </div>
            <div class="action-icon-wrapper" v-if="isInMoveRange">
                <span class="btn-icon" @click="onCommand('move')">
                    <fa-icon icon="horse-head" />
                </span>
            </div>
            <div
                class="action-icon-wrapper"
                v-if="isInChargeRange"
                @click="onSelectChargePath(position)"
            >
                <span class="btn-icon">
                    <icon class="horseshoe" icon="horseshoe" />
                </span>
                <fa-icon
                    class="action-icon-super confirmation"
                    icon="check-circle"
                    v-if="isSelectedChargePosition"
                ></fa-icon>
            </div>
            <div class="action-icon-wrapper"  v-if="canBeControlled">
                <span class="btn-icon control" @click="onCommand('control')">
                    <fa-icon icon="flag" />
                </span>
            </div>
            <div class="action-icon-wrapper attack-fort-wrapper" v-if="fortIsAttackable">
                <span
                    class="btn-icon attack-fort"
                    @click="onCommand('attack_fort')"
                >
                    <icon class="action-icon attack-fort-icon" icon="fort" />
                    <span class="action-icon slash-wrapper">
                        <span class="action-icon-slash" />
                        <span class="action-icon-slash" />
                    </span>
                </span>
            </div>
            <div class="action-icon-wrapper" v-if="fortIsSacrificeAttackable">
                <span
                    class="btn-icon attack-fort"
                    @click="onCommand('sacrifice_attack_fort')"
                >
                    <icon class="action-icon attack-fort-icon" icon="fort" />
                    <span class="action-icon slash-wrapper">
                        <span class="action-icon-slash" />
                        <span class="action-icon-slash" />
                    </span>
                    <fa-icon class="action-icon drop" icon="tint" />
                </span>
            </div>
            <div class="action-icon-wrapper" v-if="fortCanBeBuilt">
                <span
                    class="btn-icon-group build-fort"
                    @click="onCommand('build_fort')"
                >
                    <fa-icon class="action-icon plus-icon" icon="plus" />
                    <icon class="action-icon fort-icon" icon="fort" />
                </span>
            </div>
            <unit-stack :units="units" :v-if="units.length" />
        </div>
        <div class="fort" v-if="fortInPosition">
            <div class="fort-wall">
                <div
                    class="crenellation"
                    v-for="crenellation in fortCrenellations" :key="`crenellation-${crenellation}`"
                >
                </div>
            </div>
        </div>

		<div class="targets-modal-overlay" v-if="uniqueUnits.length >= 2 && actionsState.showTargetsModal">
			<div class="targets-modal">
				<div class="targets">
					<unit-coin
						v-for="(unit, index) of uniqueUnits"
						:key="`unit-${index}`"
						:unit="unit"
						:showActions="true"
						:index="index"
					/>
				</div>
				<fa-icon
					icon="times"
					class="btn-icon close-targets-modal"
					@click="closeTargetsModal"
				/>
			</div>
		</div>
    </div>
</template>
<script>
import find from 'lodash.find';
import { getAssetUrl } from '../../helpers/assets';
import {
    CONTROL_BASES,
    FOUR_PLAYER_TILES,
} from '../Game/Game-types';
import { UNIT_TYPE_LONGBOAT } from '../Unit/Unit-types';

export default {
    props: ['gameState', 'position', 'units'],
    name: 'Tile',
    data() {
        return {
            fortCrenellations: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
        };
    },
    computed: {
        actions() {
            return this.$store.state.actionsState.actions;
        },
        actionsState() {
            return this.$store.state.actionsState;
        },
        activeTile() {
			if (this.units.length >= 2 && this.units.find(u => u.type === UNIT_TYPE_LONGBOAT)) {
				return this.units.some(u => u.id === this.selectedUnitId ||
					u.id === this.friendlyUnitId);
			}

            return this.unit && (this.unit.id === this.selectedUnitId ||
                this.unit.id === this.friendlyUnitId);
        },
        canBeControlled() {
            const controlActions = this.selectedUnitActions.filter(action => action.type === 'control');
            let controlPositions = [];

            controlActions.map((action) => {
                controlPositions = controlPositions.concat(action.targetPositions);
            });

            return controlPositions.includes(this.position);
        },
        canBeDeployedTo() {
            const deployAction = find(this.selectedUnitActions, { type: 'deploy' });
            return deployAction && deployAction.targetPositions.includes(this.position);
        },
        controlMarker() {
            for (const team of this.gameState.teams) {
                if (team.controlledBases.includes(this.position)) {
                    return team.color;
                }
            }

            return false;
        },
        forts() {
            return this.gameState.forts ? this.gameState.forts : [];
        },
        fortCanBeBuilt() {
            const buildFortAction = find(this.selectedUnitActions, { type: 'build_fort' });
            return buildFortAction && buildFortAction.targetPositions.includes(this.position);
        },
        fortInPosition() {
            return find(this.forts, { position: this.position });
        },
        fortIsAttackable() {
            return this.fortCanBeAttacked('attack_fort');
        },
        fortIsSacrificeAttackable() {
            return this.fortCanBeAttacked('sacrifice_attack_fort');
        },
        friendlyUnitId() {
            return this.$store.state.actionsState.friendlyUnitId;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        hasAction() {
            return this.isInMoveRange ||
                this.canBeDeployedTo ||
                this.canBeControlled ||
                this.isInChargeRange ||
                this.fortIsAttackable ||
                this.fortIsSacrificeAttackable ||
                this.fortCanBeBuilt;
        },
        hasManeuverAction() {
            return this.units.some(unit => this.selectedUnitActions.some(action =>
                ['maneuver', 'shocked_maneuver'].includes(action.type) &&
                action.friendlyUnitId === unit.id,
            ));
        },
        isControlBase() {
            return CONTROL_BASES.includes(this.position);
        },
        isFourPlayerTile() {
            return FOUR_PLAYER_TILES.includes(this.position);
        },
        isInChargeRange() {
            let chargeRange = [];

            this.selectedUnitActions.map((action) => {
                if (['attack', 'attack_fort', 'poison'].includes(action.type) &&
                    action.unitId === this.selectedUnitId &&
                    action.friendlyUnitId === this.friendlyUnitId) {

					if (action.type === 'poison' || action.targetPositions.length > 1) {
						chargeRange = chargeRange.concat(action.targetPositions);
					}
                }
            });

            return chargeRange.includes(this.position);
        },
        isInMoveRange() {
            const moveAction = find(this.selectedUnitActions, {
                type: 'move',
                unitId: this.selectedUnitId,
                friendlyUnitId: this.friendlyUnitId,
            });

            return moveAction && moveAction.targetPositions.includes(this.position);
        },
        isGame() {
            return this.$route.name === 'game';
        },
        isSacrificeControl() {
            return this.selectedUnitActions.find(action =>
                action.type === 'control' &&
                action.targetPositions.includes(this.position) &&
                action.sacrificeControl,
            );
        },
        isSelectedChargePosition() {
            return this.isInChargeRange && this.actionsState.selectedPosition === this.position;
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
		longBoatAtPosition() {
			return this.units.find(u =>
				u.position &&
				u.type === UNIT_TYPE_LONGBOAT,
			);
		},
        player() {
            return this.isGame ? this.$store.getters.player : this.$store.getters.replayPlayer;
        },
        players() {
            return this.gameState.players;
        },
        selectedUnitActions() {
            return this.actionsState.selectedUnitActions;
        },
        selectedUnitId() {
            return this.$store.state.actionsState.selectedUnitId;
        },
        submittingAction() {
            return this.actionsState.submitting;
        },
        theme() {
            return this.gameState.theme;
        },
        tileActions() {
            let actions = 0;
            let addPoisonable = false;

            if (this.isInMoveRange) {
                actions++;
            }

            if (this.hasManeuverAction) {
                actions++;
            }

            if (this.canBeDeployedTo) {
                actions++;
            }

            if (this.canBeControlled) {
                actions++;
            }

            if (this.isInChargeRange) {
                actions++;
            }

            if (this.fortIsAttackable) {
                addPoisonable = true;
                actions++;
            }

            if (this.fortIsSacrificeAttackable) {
                addPoisonable = true;
                actions++;
            }

            if (this.fortCanBeBuilt) {
                actions++;
            }

            if (addPoisonable && this.poisonable) {
                actions++;
            }

            if (this.mimicableUnitOnPosition) {
                actions++;
            }

            return actions;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        unit() {
            return this.units.find(u => u.position);
        },
        poisonable() {
            return this.unit && this.selectedUnitActions.filter(action =>
                action.type === 'poison' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        mimicableUnitOnPosition() {
            return this.unit && this.selectedUnitActions.find(action =>
                action.type === 'mimic' &&
                action.friendlyUnitId === this.unit.id,
            );
        },
		uniqueUnits() {
			if (!this.longBoatAtPosition) {
				return [];
			}

			const typeMap = this.units.reduce((acc, unit) => {
				if (!acc[unit.type]) acc[unit.type] = [];
				acc[unit.type].push(unit);
				return acc;
			}, {});

			const result = [];

			Object.entries(typeMap).forEach(([_type, group]) => {
				if (group.length === 1) {
					result.push(group[0]);
				} else {
					const unitWithPosition = group.find(u => u.position);

					if (unitWithPosition) {
						result.push(unitWithPosition);
					}
				}
			});

			return result;
		},
    },
    methods: {
        getAssetUrl,
        async onCommand(actionType) {
			let friendlyUnitId;

            if (actionType === 'attack_fort' || actionType === 'sacrifice_attack_fort') {
                this.$store.dispatch('setFortId', this.fortInPosition.id);

                const fortAttackAction = find(this.actionsState.selectedUnitActions, {
                    unitId: this.actionsState.selectedUnitId,
                    fortId: this.fortInPosition.id,
                });

                const targetPosition = this.actionsState.selectedPosition;

                if (fortAttackAction &&
                    fortAttackAction.targetPositions.length &&
                    !fortAttackAction.targetPositions.includes(targetPosition)) {
                    this.$store.dispatch('setSelectedPosition', fortAttackAction.targetPositions[0]);
                }
            } else {
                this.$store.dispatch('setSelectedPosition', this.position);

				if (['control', 'build_fort'].includes(actionType) &&
					this.units.length >= 2 && this.units[0].type === UNIT_TYPE_LONGBOAT) {
					friendlyUnitId = this.units.find(u =>
					u.id === this.actionsState.friendlyUnitId,
				)?.id || this.unit.id;
				} else if (this.unit) {
					friendlyUnitId = this.unit.id;
				}

                if (actionType !== 'move' && this.unit) {
                    this.$store.dispatch('setFriendlyUnitId', friendlyUnitId);
                }
            }

            await this.$store.dispatch('sendCommand',
                {
                    actionType,
                    gameId: this.gameId,
                    playerId: this.player.id,
                    $socket: this.$socket,
                    $notify: this.$notify,
                },
            );
        },
        onSelectChargePath(position) {
            if (this.actionsState.selectedPosition === position) {

				const isPoisonChargePosition = this.actionsState.actions.find(action =>
					action.type === 'position' && action.targetPositions[0] === position,
				);

				if (isPoisonChargePosition) {
					return;
				}

				this.$store.dispatch('setSelectedPosition', null);
            } else {
                this.$store.dispatch('setSelectedPosition', position);
            }
        },
        fortCanBeAttacked(actionType) {
            const attackFortActions = this.selectedUnitActions.filter(action =>
                action.type === actionType &&
                action.friendlyUnitId === this.friendlyUnitId,
            );
            const attackableFortIds = attackFortActions.map(action => action.fortId);
            return this.fortInPosition && attackableFortIds.includes(this.fortInPosition.id);
        },
		closeTargetsModal() {
			this.$store.dispatch('setShowTargetsModal', false);
		},
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .tile {
        align-items: center;
        display: flex;
        height: 100%;
        justify-content: center;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;

        &:before {
            content: '';
            width: 100%;
            height: 100%;
            background-color: $tile;
            clip-path: polygon(75% 0,100% 50%,75% 100%,25% 100%,0 50%,25% 0);
        }

        &.action-count-2 {

            .inner-wrapper {

                .action-icon-wrapper {

                    &:first-of-type {
                        left: 25%;
                        transform: translate(-45%, -50%);
                    }

                    &:nth-of-type(2) {
                        left: 75%;
                        transform: translate(-55%, -50%);
                    }
                }
            }
        }

        &.action-count-2.has-maneuver-action {

            .inner-wrapper {

                :deep(.unit-stack .combat-actions-group) {
                    left: 75%;
                }
            }
        }

        &.action-count-3 {

            .inner-wrapper {

                .action-icon-wrapper {

                    .btn-icon {
                        height: 90%;
                        width: 90%;
                    }

                    &:first-of-type {
                        left: 25%;
                        transform: translate(-45%, -75%);
                    }

                    &:nth-of-type(2) {
                        left: 75%;
                        transform: translate(-55%, -75%);
                    }

                    &:nth-of-type(3) {
                        top: 75%;
                        transform: translate(-50%, -50%);
                        right: 0;
                    }
                }
            }
        }

        &.action-count-3.has-maneuver-action {

            .inner-wrapper {

                :deep(.unit-stack .combat-actions-group) {
                    left: 50%;
                    top: 75%;
                }
            }
        }

        &.active-tile {

            .inner-wrapper {
                z-index: 100;
            }
        }

        .inner-wrapper {
            height: 100%;
            position: absolute;
            width: 90%;
            left: 50%;
            top: 0;
            transform: translateX(-50%);
            z-index: 2;

            .action-icon-wrapper {
                cursor: pointer;
                height: 42%;
                left: 50%;
                position: absolute;
                top: 50%;
                transform: translate(-50%, -50%);
                user-select: none;
                width: 42%;
                z-index: 10;

                .btn-icon-group {
                    height: 100%;
                    width: 100%;

                    .arrow-icon {
                        width: 22%;
                    }

                    .helmet-icon {
                        margin-left: 10%;
                        width: 35%;
                    }

                    .plus-icon {
                        width: 22%;
                    }

                    .fort-icon {
                        margin-left: 10%;
                        width: 44%;
                    }
                }

                .btn-icon {
                    padding: 16%;
                    position: relative;
                    height: 100%;
                    width: 100%;

                    &.control {
						padding: 20%;

                        .sacrifice-control {
                            color: $dark-blue;
                            fill: $dark-blue;
                            height: auto;
                            position: absolute;
                            left: 53%;
                            transform: translateX(-50%);
                            top: 29%;
                            width: 27%;
                        }
                    }

                    .slash-wrapper {
                        position: absolute;
                        width: 70%;
                        height: 70%;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        transform: rotate(45deg);

                        .action-icon-slash {
                            align-self: center;
                            background: white;
                            border-radius: 1px;
                            display: block;
                            height: 8%;
                            width: 100%;

                            &:nth-of-type(2) {
                                background: $dark-blue;
                            }
                        }
                    }

                    .drop {
                        height: auto;
                        position: absolute;
                        right: 13%;
                        top: 20%;
                        width: 13%;
                    }
                }

                .action-icon-super {
                    font-size: .6em;
                    position: absolute;
                    top: -5%;
                    right: -10%;

                    &.confirmation {
                        background: white;
                        border-radius: 50%;
                        color: $grass-green;
                        z-index: 5;
                    }
                }
            }

            .unit-stack {
                left: 50%;
                max-height: 90%;
                max-width: 90%;
                position: absolute;
                top: 50%;
                transform: translate(-50%, -50%);
                width: 100%;
            }
        }

        .fort {
            height: 100%;
            width: 90%;
            position: absolute;
            z-index: 1;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);

            .fort-wall {
                position: absolute;
                width: 78%;
                height: 78%;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                border-radius: 50%;
                border: 3px solid $fort-gray;

                .crenellation {
                    position: absolute;
                    transform: translate(-50%, -50%);
                    width: 120%;
                    height: 15%;
                    top: 50%;
                    left: 50%;
                    $degrees: 0;

                    @for $i from 1 through 14 {
                        &:nth-of-type(#{$i}) {
                            transform: translate(-50%, -50%) rotate($degrees + deg);
                        }

                        $degrees: $degrees + 30;
                    }

                    &:after {
                        background: $fort-gray;
                        content: '';
                        height: 100%;
                        width: 5%;
                        position: absolute;
                        left: 0;
                    }

                    &:before {
                        background: $fort-gray;
                        content: '';
                        height: 100%;
                        width: 5%;
                        position: absolute;
                        right: 0;
                    }
                }
            }
        }

        &.black {

            .fort-wall {
                border-color: $dark-gray;

                .crenellation {

                    &:after {
                        background: $dark-gray;
                    }

                    &:before {
                        background: $dark-gray;
                    }
                }
            }

            &.tk {

                .fort-wall {
                    border-color: $bear-peach;

                    .crenellation {

                        &:after {
                            background: $bear-peach;
                        }

                        &:before {
                            background: $bear-peach;
                        }
                    }
                }
            }
        }

        &.white {

            .fort-wall {
                border-color: white;

                .crenellation {

                    &:after {
                        background: white;
                    }

                    &:before {
                        background: white;
                    }
                }
            }

            &.tk {

                .fort-wall {
                    border-color: $deer-gray;

                    .crenellation {

                        &:after {
                            background: $deer-gray;
                        }

                        &:before {
                            background: $deer-gray;
                        }
                    }
                }
            }
        }

        &.dark {

            &:before {
                background-color: $dark-tile;
            }

            &.in-move-range {

                &:before {
                    background-color: $dark-tile-shaded;
                }
            }
        }

        &.in-move-range {
            cursor: pointer;

            &:before {
                background-color: $tile-shaded;
            }

            .btn-icon {
                color: white;
                background: $dark-blue;
                padding: 7px;
                font-size: 1.2em;
            }
        }

        &.in-charge-range {

            .btn-icon {
                color: white;
                background: $dark-blue;
                padding: 7px;
                font-size: 1.2em;

                &.horseshoe {
                    width: 22px;
                    height: 22px;
                    padding-top: 5px;
                    padding-bottom: 3px;
                    min-width: auto;
                    min-height: auto;
                    padding: 6px 5px 3px 5px;
                }
            }
        }

        &.control-base {

            &:not(.controlled) {

                &::after {
                    background-image: url('../../assets/control_base.svg');
                    background-position: center;
                    background-repeat: no-repeat;
                    background-size: 72%;
                    content: '';
                    opacity: 0.7;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    right: 0;
                    position: absolute;
                }

                &.occupied {

                    &::after {
                        background-size: 80%;
                    }
                }
            }

            &.black {

                .btn-icon,
                .btn-icon-group {
                    background: $light-gray;

                    > * {
                        color: $dark-blue;
                        fill: $dark-blue;
                    }

                    .action-icon,
                    .action-icon-secondary {
                        color: $dark-blue;
                        fill: $dark-blue;
                    }

                    &.bolster {

                        .plus-icon {
                            color: $light-gray;
                        }
                    }
                }

                .action-icon-wrapper {
                    user-select: none;

                    .btn-icon-group {

                        &.attack-fort {

                            .action-icon-secondary {

                                .action-icon-slash {
                                    background: $dark-blue;
                                    border-radius: 1px;
                                    display: block;
                                    height: 100%;
                                    left: 50%;
                                    position: absolute;
                                    top: 50%;
                                    transform: translate(-50%, -50%);
                                    width: 2px;

                                    &:nth-of-type(2) {
                                        background: $light-gray;
                                        left: calc(50% - 2px);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            &.controlled {

                &.white {

                    &:before {
                        background: $tile-white;
                        background-image: linear-gradient(to bottom right, $tile-white-alt, $tile-white);
                    }

                }

                &.black {

                    &:before {
                        background: $tile-black;
                        background-image: linear-gradient(to bottom right, $tile-black, $medium-gray);
                    }
                }

                &.tk {

                    &:before {
                        background: none;
                        background-image: none;
                    }
                }
            }
        }

        &.fortified.is-attackable {

            &.mimicable-unit {

                .attack-fort-wrapper {
                    height: 36%;
                    width: 36%;
                }
            }
        }

        .control-marker {
            height: 100%;
            left: 0;
            position: absolute;
            top: 0;
            width: 100%;

            img {
                height: auto;
                left: 50%;
                position: absolute;
                top: 50%;
                transform: translate(-50%, -50%);
                user-select: none;
                width: 75%;
            }

            .alt-img {
                width: 101%;
            }
        }
    }

    .grid-row {

        &:first-of-type {
            .tile {

                &:first-of-type {

                    .hexagon-outer,
                    .hexagon-inner {
                        visibility: hidden;
                    }
                }
            }
        }

        &:last-of-type {

            .tile {

                &:last-of-type {

                    .hexagon-outer,
                    .hexagon-inner {
                        visibility: hidden;
                    }
                }
            }
        }
    }

	.targets-modal-overlay {
		position: absolute;
		left: 50%;
		top: 25%;
		transform: translate(-50%, -80%);
		width: 200%;
		height: 100%;
		z-index: 200;

		.targets-modal {
			background: rgba(255, 255, 255, .95);
			border-radius: 15px;
			padding: 5px 10px;
			display: flex;
			flex-direction: column;
			width: 90%;
			height: auto;
			margin: 0 auto;

			.targets-modal-title {
				font-size: .6rem;
				font-weight: bold;
			}

			.close-targets-modal {
				position: absolute;
				right: -14px;
				top: -4px;
				width: 14px;
				height: 14px;
				background: rgba(255, 255, 255, .95);

				> * {
					color: $royal-coin-black;
					fill: $royal-coin-black;
				}
			}

			.targets {
				display: flex;

				.unit-coin {
					position: relative;
					left: auto;
					top: auto;
					width: auto;
				}
			}
		}
	}

@media only screen and (min-width: $screen-xs) {
    .tile {

        .inner-wrapper {

            .action-icon-wrapper {

                .action-icon-super {
                    top: -5%;
                    right: -5%;
                    font-size: .65em;
                }
            }
        }
    }

	.targets-modal-overlay {

		.targets-modal {

			.targets-modal-title {
				font-size: .75rem;
				font-weight: bold;
			}

			.close-targets-modal {
				position: absolute;
				right: -12px;
				top: -4px;
				width: 15px;
				height: 15px;
			}
		}
	}
}
</style>
